import React, { useState, useEffect, useRef } from 'react';
import useClickOutside from '../../../../../hooks/useClickOutside';
import { Box } from '@mui/system';
import { datadogLogs } from '@datadog/browser-logs';
import QuickRepliesCountrySelector from './QuickRepliesCountrySelector';
import { Country } from '../../../../../models/Country';
import { Button } from '@mui/material';
import { useAppContext } from '../../../../../AppContext';

export interface QuickRepliesComponentProps {
  setShowReplies: (value: boolean) => void;
  quickReplies: connect.QuickResponse[];
}

interface QuickRepliesContainerProps {
  channel: 'chat' | 'email';
  selectedContact: string | null;
  QuickRepliesComponent: React.ComponentType<QuickRepliesComponentProps>;
}

const QuickRepliesContainer = ({
  channel,
  selectedContact,
  QuickRepliesComponent
}: QuickRepliesContainerProps): JSX.Element => {
  const { agent } = useAppContext();
  const [showReplies, setShowReplies] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [quickReplies, setQuickReplies] = useState<connect.QuickResponse[]>([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
  const agentName = agent?.getName();
  const isDisabled = !selectedContact;

  const toggleReplies = (): void => {
    setShowReplies(!showReplies);
  };

  useEffect(() => {
    const namePlaceholderRegex = /\[NAAM\]/g;

    if (isDisabled) {
      return;
    }

    connect.QuickResponses.searchQuickResponses({ query: selectedCountryCode ? `${selectedCountryCode}_` : '' })
      .then(response => {
        const channelFilteredReplies = response.results.filter(reply => {
          const channels = reply.channels?.map((ch: string) => ch.toLowerCase()) || [];
          const hasChannel = channels.includes(channel);

          // TODO: Remove this condition once AWS supports channel-specific quick replies for chat and email.
          // This is to prevent showing chat quick replies in email and vice versa until AWS provides the necessary support.
          if (channel === 'chat' && channels.includes('chat') && channels.includes('email')) {
            return false;
          }

          return hasChannel;
        });

        const updatedQuickReplies = channelFilteredReplies.map(reply => {
          if (reply.contents.plainText && agentName) {
            reply.contents.plainText.content = reply.contents.plainText.content.replace(
              namePlaceholderRegex,
              agentName
            );
          }
          return reply;
        });

        setQuickReplies(updatedQuickReplies);
      })
      .catch((error: Error) => {
        datadogLogs.logger.error(`Unable to retrieve Quick Replies`, { quickReplies: true, channel }, error);
      });
  }, [selectedCountryCode, isDisabled, agentName, channel]);

  useClickOutside(containerRef, showReplies, () => setShowReplies(false));

  const handleCountrySelect = React.useCallback((country: Country): void => {
    setSelectedCountryCode(country.country);
  }, []);

  return (
    <Box ref={containerRef}>
      <Box sx={{ display: showReplies ? 'block' : 'none' }}>
        <QuickRepliesComponent setShowReplies={setShowReplies} quickReplies={quickReplies} />
      </Box>
      <QuickRepliesCountrySelector onCountrySelect={handleCountrySelect} disabled={isDisabled} channel={channel} />
      <Button
        data-testid={`${channel}-quick-replies-button`}
        sx={{
          height: '48px',
          padding: '0 16px',
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          border: '1px solid #DDDDDD',
          background: showReplies ? '#F2F7FC' : 'inherit',
          borderColor: showReplies ? '#0090E3' : '#DDDDDD',
          marginRight: '8px'
        }}
        disabled={isDisabled}
        onClick={toggleReplies}
      >
        Quick Replies
      </Button>
    </Box>
  );
};

export default QuickRepliesContainer;
